import { Suspense, lazy } from "react";
import { Toaster } from "@/components/ui/toaster";
import { Toaster as Sonner } from "@/components/ui/sonner";
import { TooltipProvider } from "@/components/ui/tooltip";
import { QueryClient, QueryClientProvider } from "@tanstack/react-query";
import { BrowserRouter, Routes, Route } from "react-router-dom";
import { ScrollToTop } from "./components/ScrollToTop";
import { usePageTracking } from "./hooks/usePageTracking";
import Index from "./pages/Index";

// Lazy load all non-critical pages to reduce initial bundle size
const About = lazy(() => import("./pages/About"));
const Contact = lazy(() => import("./pages/Contact"));
const Consultancy = lazy(() => import("./pages/Consultancy"));
const AI = lazy(() => import("./pages/AI"));
const Data = lazy(() => import("./pages/Data"));

const Automations = lazy(() => import("./pages/Automations"));
const PrivacyPolicy = lazy(() => import("./pages/PrivacyPolicy"));
const Compliance = lazy(() => import("./pages/Compliance"));
const NotFound = lazy(() => import("./pages/NotFound"));
const WebsiteBuild = lazy(() => import("./pages/WebsiteBuild"));
const MinimalAgency = lazy(() => import("./pages/showcase/MinimalAgency"));
const SaaSLanding = lazy(() => import("./pages/showcase/SaaSLanding"));
const PersonalBrand = lazy(() => import("./pages/showcase/PersonalBrand"));
const Ecommerce = lazy(() => import("./pages/showcase/Ecommerce"));
const CyberDark = lazy(() => import("./pages/showcase/CyberDark"));
const Restaurant = lazy(() => import("./pages/showcase/Restaurant"));
const Blog = lazy(() => import("./pages/showcase/Blog"));
const RealEstate = lazy(() => import("./pages/showcase/RealEstate"));

const queryClient = new QueryClient();

// Simple loading fallback
const PageLoader = () => (
  <div className="min-h-screen flex items-center justify-center bg-background">
    <div className="w-8 h-8 border-2 border-primary border-t-transparent rounded-full animate-spin" />
  </div>
);

// Component to track page views
const PageTracker = () => {
  usePageTracking();
  return null;
};

const App = () => (
  <QueryClientProvider client={queryClient}>
    <TooltipProvider>
      <Toaster />
      <Sonner />
      <BrowserRouter>
        <PageTracker />
        <ScrollToTop />
        <Suspense fallback={<PageLoader />}>
          <Routes>
            <Route path="/" element={<Index />} />
            <Route path="/about" element={<About />} />
            <Route path="/contact" element={<Contact />} />
            <Route path="/consultancy" element={<Consultancy />} />
            <Route path="/ai" element={<AI />} />
            <Route path="/data" element={<Data />} />
            
            <Route path="/automations" element={<Automations />} />
            <Route path="/privacy-policy" element={<PrivacyPolicy />} />
            <Route path="/compliance" element={<Compliance />} />
            <Route path="/website-build" element={<WebsiteBuild />} />
            <Route path="/showcase/minimal-agency" element={<MinimalAgency />} />
            <Route path="/showcase/saas-landing" element={<SaaSLanding />} />
            <Route path="/showcase/personal-brand" element={<PersonalBrand />} />
            <Route path="/showcase/ecommerce" element={<Ecommerce />} />
            <Route path="/showcase/cyber-dark" element={<CyberDark />} />
            <Route path="/showcase/restaurant" element={<Restaurant />} />
            <Route path="/showcase/blog" element={<Blog />} />
            <Route path="/showcase/real-estate" element={<RealEstate />} />
            {/* ADD ALL CUSTOM ROUTES ABOVE THE CATCH-ALL "*" ROUTE */}
            <Route path="*" element={<NotFound />} />
          </Routes>
        </Suspense>
      </BrowserRouter>
    </TooltipProvider>
  </QueryClientProvider>
);

export default App;
